import React, { useContext, useMemo } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { useTheme } from "react-native-elements";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import { getActionsForAreaByCategory } from "../backend/actions/Actions";
import { BuildingActionBase } from "../backend/buildings/Building";
import { hasFlag } from "../backend/Flags";
import { getFirstHint } from "../backend/hints/Hints";
import { PlayerContext } from "../backend/PlayerContext";
import { getResourceCap, Resource } from "../backend/Resources";
import { formatTime } from "../utils/FormattingUtils";
import ActionsContainer from "./utility/ActionsContainer";
import CategoryContainer from "./utility/CategoryContainer";
import { useGestureHandler } from "./utility/Miscellaneous";
import { ProgressBar } from "./utility/ProgressBar";
import { Text } from "./utility/Text";

export default function Campus(props: {
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
}) {
  const playerContext = useContext(PlayerContext);
  const currentHint = getFirstHint(playerContext);
  const hintProgress = currentHint?.progress?.(playerContext);

  const swipeHandlerGesture = useGestureHandler(
    props.onSwipeLeft,
    props.onSwipeRight,
  );
  const nativeGesture = useMemo(() => Gesture.Native(), []);
  const compositeGesture = useMemo(
    () => Gesture.Simultaneous(swipeHandlerGesture, nativeGesture),
    [swipeHandlerGesture, nativeGesture],
  );

  const swipeHandlerGestureBottom = useGestureHandler(
    props.onSwipeLeft,
    props.onSwipeRight,
  );
  const nativeGestureBottom = useMemo(() => Gesture.Native(), []);
  const compositeGestureBottom = useMemo(
    () => Gesture.Simultaneous(swipeHandlerGestureBottom, nativeGestureBottom),
    [swipeHandlerGestureBottom, nativeGestureBottom],
  );

  const allActionsMaxedOut = !Object.values(
    getActionsForAreaByCategory("Campus"),
  )
    .map((actionRecord) => Object.values(actionRecord))
    .flat()
    .some((action) => {
      if (
        !(action instanceof BuildingActionBase) ||
        !action.isVisible(playerContext)
      ) {
        return false;
      }
      const cost = action.getCost(playerContext);
      const isFree =
        Object.keys(cost.resources).length === 0 &&
        Object.keys(cost.items).length === 0;
      const capIsEnough = (
        Object.keys(cost.resources) as Array<Resource>
      ).every((resource: Resource) => {
        return (
          (cost.resources[resource] ?? 0) <=
          getResourceCap(playerContext, resource)
        );
      });
      if (capIsEnough) {
        return true;
      }
    });

  const showCampusHint =
    allActionsMaxedOut &&
    (currentHint?.description == "Explore the School Basement" ||
      currentHint?.description == "Explore the Thunder Tower" ||
      currentHint?.description ==
        "Purchase the elemental upgrades and explore the new dungeons") &&
    (!hasFlag(playerContext, "upgrades_unlocked") ||
      !Object.values(getActionsForAreaByCategory("Upgrades"))
        .map((actionRecord) => Object.values(actionRecord))
        .flat()
        .some((action) => {
          return action.isVisible(playerContext);
        }));

  const theme = useTheme();
  const campusHint = useMemo(() => {
    if (showCampusHint) {
      return (
        <View style={styles.campusHintWarning}>
          <Text style={{ color: theme.theme.colors.warning }}>
            It looks like you have built everything you can for the time being.
            Make progress in Exploration to find ways to build more!
          </Text>
        </View>
      );
    }
    return null;
  }, [showCampusHint, theme]);

  const showPastHint = Date.now() < playerContext.lastProcessedTime - 5.0;
  const pastHint = useMemo(() => {
    if (showPastHint) {
      return (
        <View style={styles.campusHintWarning}>
          <Text style={{ color: theme.theme.colors.warning }}>
            The game has run in the future! Time will be stopped until that
            future time has been reached. You can solve this by retiring,
            changing your system time back to the future, or waiting{" "}
            {formatTime(
              (playerContext.lastProcessedTime - Date.now()) / 1000.0,
            )}
            .
          </Text>
        </View>
      );
    }
    return null;
  }, [showPastHint, theme, showPastHint && Date.now()]);

  return useMemo(
    () => (
      <View style={styles.campusContainer}>
        <GestureDetector key="campusGesture" gesture={compositeGesture}>
          <ScrollView
            contentContainerStyle={styles.contentContainer}
            style={styles.container}
          >
            {campusHint}
            {pastHint}
            <ActionsContainer area="Campus" useSubcategories={true} />
          </ScrollView>
        </GestureDetector>
        <GestureDetector key="hintGesture" gesture={compositeGestureBottom}>
          <View
            style={[
              styles.hintContainer,
              { borderColor: theme.theme.colors.divider },
            ]}
          >
            <CategoryContainer
              titleStyle={styles.categoryTitle}
              title="Current Goal"
              containerStyle={styles.categoryContainer}
              disableExpand={true}
            >
              <View style={styles.hintContents}>
                <Text style={styles.hintContentsText}>
                  {currentHint?.description}!
                </Text>
                {currentHint?.progress && (
                  <ProgressBar
                    progress={hintProgress}
                    width={null}
                    useNativeDriver={true}
                    style={styles.hintProgress}
                  />
                )}
              </View>
            </CategoryContainer>
          </View>
        </GestureDetector>
      </View>
    ),
    [
      currentHint,
      campusHint,
      pastHint,
      hintProgress,
      compositeGesture,
      compositeGestureBottom,
      theme,
    ],
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
  },
  contentContainer: {
    alignItems: "stretch",
    justifyContent: "flex-start",
    width: "100%",
    paddingHorizontal: 8,
  },
  hintContents: {
    marginLeft: 16,
    marginTop: 8,
    paddingRight: 32,
  },
  hintContentsText: {
    fontStyle: "italic",
  },
  hintProgress: {
    marginTop: 4,
  },
  hintContainer: {
    borderTopWidth: 1,
  },
  categoryTitle: {
    marginLeft: 8,
  },
  categoryContainer: {
    paddingTop: 4,
    marginBottom: 8,
  },
  campusContainer: {
    height: "100%",
    alignItems: "stretch",
    justifyContent: "flex-start",
    width: "100%",
  },
  campusHintWarning: {
    marginTop: 16,
  },
});
